/**
 * Register - 3-Step Registration Wizard (Split-Screen Layout)
 * Creates user + workspace + assigns free tier
 *
 * WCAG 2.2 AA compliant
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { Alert } from '@/components/v2/ui/Alert';
import { RegisterStep1 } from './RegisterStep1';
import { RegisterStep2 } from './RegisterStep2';
import { RegisterStep3 } from './RegisterStep3';

interface FormData {
  email: string;
  password: string;
  username: string;
  company_name: string;
  workspace_name: string;
}

interface ApiResponse {
  success: boolean;
  user_id?: string;
  workspace_id?: string;
  tier_code?: string;
  error?: string;
}

export default function Register() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const logoPath = theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg';
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    username: '',
    company_name: '',
    workspace_name: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStep1Complete = (data: { email: string; password: string; username: string }) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setError(null);
    setStep(2);
  };

  const handleStep2Complete = async (data: { company_name: string; workspace_name: string }) => {
    const fullFormData = { ...formData, ...data };
    setFormData(fullFormData);
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/v2/auth/register-with-workspace', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fullFormData),
      });

      const result: ApiResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || t('errors.creating'));
      }

      // Success - Move to step 3 (email verification required)
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setError(null);
    setStep((prev) => Math.max(1, prev - 1));
  };

  return (
    <div className="register-page min-h-screen flex">
      {/* Left Side - Registration Form */}
      <div className="register-form-container flex-1 flex flex-col justify-between px-12 py-8 lg:px-24 bg-white dark:bg-neutral-950">
        {/* Logo */}
        <div className="register-logo">
          <Link to="/" className="flex items-center">
            <img
              src={logoPath}
              alt="GOKI Logo"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Form Content */}
        <div className="register-form-content max-w-sm w-full mx-auto space-y-8">
          {/* Header */}
          <div className="register-form-header space-y-2">
            {step < 3 && (
              <div className="flex items-center gap-2 mb-4">
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                    aria-label={t('common.back')}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {step} / 3
                </span>
              </div>
            )}
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
              {step === 1 && 'Konto erstellen'}
              {step === 2 && 'Workspace einrichten'}
              {step === 3 && 'Fast geschafft!'}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {step === 1 && 'Erstelle deinen GOKI Account'}
              {step === 2 && 'Richte deinen ersten Workspace ein'}
              {step === 3 && t('auth.confirmEmail')}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="register-error">
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <div className="register-step-content">
            {step === 1 && <RegisterStep1 onComplete={handleStep1Complete} initialData={formData} />}
            {step === 2 && (
              <RegisterStep2
                onComplete={handleStep2Complete}
                initialData={formData}
                isSubmitting={isSubmitting}
              />
            )}
            {step === 3 && <RegisterStep3 />}
          </div>

          {/* Login Link (only on step 1) */}
          {step === 1 && (
            <div className="register-login-link text-center">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Hast du bereits einen Account?{' '}
                <Link
                  to="/v2/login"
                  className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  Jetzt anmelden
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="register-footer text-center text-sm text-neutral-500 dark:text-neutral-600 space-x-4">
          <Link to="/impressum" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            Impressum
          </Link>
          <span>|</span>
          <Link to="/datenschutz" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            Datenschutz
          </Link>
          <span>|</span>
          <Link to="/agb" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            AGB
          </Link>
        </footer>
      </div>

      {/* Right Side - Hero/Testimonial (Desktop only) */}
      <div className="register-hero hidden lg:flex flex-1 flex-col items-center justify-center p-16 bg-gradient-to-br from-primary-50 to-primary-50 dark:from-primary-950 dark:to-primary-950 relative overflow-hidden">
        {/* Decorative Gradient Orbs */}
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-96 h-96 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />

        {/* Feature List */}
        <div className="register-hero-features relative z-10 max-w-lg space-y-8">
          <h2 className="text-3xl font-bold text-neutral-900 dark:text-white">
            Starte jetzt mit GOKI
          </h2>

          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 dark:bg-primary-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  KI-generierte Produktbilder
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Erstelle professionelle Produktbilder in Sekunden
                </p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 dark:bg-primary-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  Templates & Presets
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {t('auth.useReadyTemplates')}
                </p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 dark:bg-primary-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  Team Workspaces
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Arbeite gemeinsam mit deinem Team an Projekten
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* Footer Copyright (Desktop) */}
        <div className="register-hero-footer absolute bottom-8 text-sm text-neutral-600 dark:text-neutral-500">
          © 2026 GOKI Image
        </div>
      </div>
    </div>
  );
}
